import { Injectable } from '@angular/core';
import { map, merge, Observable, Subject } from 'rxjs';

import { ExistedUsers } from '@app/shared/models';
import {
  Document,
  ModulesRole,
  TrainingCoursesRoleApiModel,
  UserProfile,
  UserTrainingCourseClassApiModel,
  SiteRoleApiModel,
  InactiveUserApiModel
} from '@app/api/api-models';
import { UserProfileSelectors } from '@app/api/api-models/user-profile/user-profile-selectors.model';
import { CheckEmailResultApiModel } from '@app/api/api-models/user-profile/CheckEmailResultApiModel';
import { ChangeInitialPasswordApiModel } from '@app/api/api-models/user-profile/ChangeInitialPasswordApiModel';
import { UserAboutInfoApiModel } from '@app/api/api-models/user-profile/UserAboutInfoApiModel';
import { UserInterestApiModel } from '@app/api/api-models/user-profile/UserInterestApiModel';
import { PersonalInformationApiModel } from '@app/api/api-models/user-profile/PersonalInformationApiModel';
import { GenderApiModel } from '@app/api/api-models/GenderApiModel';
import { AccountSettingsApiModel } from '@app/api/api-models/user-profile/AccountSettingsApiModel';
import { EmployeeSettingsApiModel } from '@app/api/api-models/user-profile/EmployeeSettingsApiModel';
import { UserBasicInformationApiModel } from '@app/api/api-models/user-profile/UserBasicInformationApiModel';
import { HttpClientService } from '@app/api/api-services/http-client.service';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  public apiUrl = 'users';
  public readonly profileChange$ = new Subject<UserBasicInformationApiModel>();
  public readonly themePreferenceChanged$ = new Subject<boolean>();

  constructor(private readonly http: HttpClientService) {}

  public getProfile(userId: string): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiUrl}/get-profile/${userId}`);
  }

  public getUserBasicInfo(userId: string): Observable<UserBasicInformationApiModel> {
    return this.http.get<UserBasicInformationApiModel>(`${this.apiUrl}/get-basic-info/${userId}`);
  }

  public getUserBasicInfoUpdatable(userId: string): Observable<UserBasicInformationApiModel> {
    return merge(
      this.getUserBasicInfo(userId),
      this.profileChange$.pipe(
        map((profile) => ({
          ...profile,
          Id: profile.Id || userId
        }))
      )
    );
  }

  public checkEmail(email: string): Observable<CheckEmailResultApiModel> {
    return this.http.get<CheckEmailResultApiModel>(`${this.apiUrl}/check-email?email=${encodeURIComponent(email)}`);
  }

  public addExistedUser(existedUsers: ExistedUsers): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/add-to-module`, existedUsers);
  }

  public addUser(newUser: UserProfile): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/add`, newUser);
  }

  public getProfileSelectors(): Observable<UserProfileSelectors> {
    return this.http.get<UserProfileSelectors>(`${this.apiUrl}/get-profile-selectors`);
  }

  public getGenders(): Observable<GenderApiModel[]> {
    return this.http.get<GenderApiModel[]>(`${this.apiUrl}/get-genders`);
  }

  public updateProfile(editedPrinter: UserProfile): Observable<UserProfile> {
    return this.http.put<UserProfile>(`${this.apiUrl}/update`, editedPrinter);
  }

  public deleteProfile(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/delete/${userId}`);
  }

  public changeInitialPassword(model: ChangeInitialPasswordApiModel): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/change-initial-password`, model);
  }

  public getAboutInfo(userId: string): Observable<UserAboutInfoApiModel> {
    return this.http.get<UserAboutInfoApiModel>(`${this.apiUrl}/get-about-info/${userId}`);
  }

  public updateAboutInfo(model: UserAboutInfoApiModel): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/update-about-info`, model);
  }

  public updateInactiveUserEmail(model: InactiveUserApiModel): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/update-inactive-user`, model);
  }

  public getUserInterests(userId: string): Observable<UserInterestApiModel[]> {
    return this.http.get<UserInterestApiModel[]>(`${this.apiUrl}/get-interests/${userId}`);
  }

  public getTrainingCourseClasses(userId: string): Observable<UserTrainingCourseClassApiModel[]> {
    return this.http.get<UserTrainingCourseClassApiModel[]>(`${this.apiUrl}/get-training-course-classes/${userId}`);
  }

  public updateUserInterests(userId: string, interestIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/update-interests/${userId}`, interestIds);
  }

  public updateSiteRole(userId: string, model: SiteRoleApiModel): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/update-site-role/${userId}`, model);
  }

  public updatePersonalInformation(data: PersonalInformationApiModel): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/update-personal-information`, data);
  }

  public updateAccountSettings(data: AccountSettingsApiModel): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/update-account-settings`, data);
  }

  public updateEmployeeSettings(data: EmployeeSettingsApiModel): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/update-employee-settings`, data);
  }

  public updateProgramRoles(id: string, roles: ModulesRole[]): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/update-program-roles/${id}`, roles);
  }

  public updateTrainingCoursesRole(id: string, model: TrainingCoursesRoleApiModel): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiUrl}/update-training-courses-role/${id}`, model);
  }

  public updateDocuments(id: string, documents: Document[]): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/update-documents/${id}`, documents);
  }

  public getUserDocuments(userId: string): Observable<Document[]> {
    return this.http.get<Document[]>(`${this.apiUrl}/get-documents/${userId}`);
  }

  public getThemePreference(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/theme-preference`);
  }

  public setThemePreference(isDarkMode: boolean): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/theme-preference`, isDarkMode);
  }
}
